import React, { useState, useContext } from 'react';
import './LifelineEventAddModal.css';
import { AppContext } from '../../../../appContext';
import { DashboardContext } from '../../DashboardContext';

interface ILifelineEventAddModalProps {
    onClose: () => void;
    onSave: (data: any) => void;
}

export const LifelineEventAddModal = ({ onClose, onSave }: ILifelineEventAddModalProps) => {
    const { translate } = useContext(AppContext);
    const { selectedClient } = useContext(DashboardContext);

    const [datetime, setDatetime] = useState('');
    const [event, setEvent] = useState('');
    const [description, setDescription] = useState('');
    const [level, setLevel] = useState<1 | 2>(1);
    const [good, setGood] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const validateDate = (dateString: string): boolean => {
        if (!dateString) return false;
        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date.getTime());
    };

    const formatDateForServer = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU');
    };

    const handleSave = async () => {
        if (!datetime.trim() || !event.trim()) {
            alert(translate('admin_fill_required'));
            return;
        }

        if (!validateDate(datetime)) {
            alert(translate('lifeline_error_save'));
            return;
        }

        setIsLoading(true);
        try {
            await onSave({
                Datetime: formatDateForServer(datetime),
                Event: event,
                Description: description,
                Level: level,
                Good: good
            });
            onClose();
        } catch (error) {
            console.error('Error creating event:', error);
            alert(translate('lifeline_create_error'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="LifelineEventAddModal_overlay" onClick={onClose}>
            <div className="LifelineEventAddModal_content" onClick={(e) => e.stopPropagation()}>
                <div className="LifelineEventAddModal_header">
                    <h3>{translate('lifeline_add_event')}</h3>
                    <button className="LifelineEventAddModal_close" onClick={onClose}>×</button>
                </div>

                <div className="LifelineEventAddModal_form">
                    <div className="LifelineEventAddModal_field">
                        <label>{translate('lifeline_event_date')}:</label>
                        <input
                            type="date"
                            value={datetime}
                            onChange={(e) => setDatetime(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>

                    <div className="LifelineEventAddModal_field">
                        <label>{translate('lifeline_event_title')}:</label>
                        <textarea
                            value={event}
                            onChange={(e) => setEvent(e.target.value)}
                            placeholder={translate('lifeline_event_title')}
                            disabled={isLoading}
                            rows={3}
                        />
                    </div>

                    <div className="LifelineEventAddModal_field">
                        <label>{translate('lifeline_event_description')}:</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={translate('lifeline_event_description')}
                            disabled={isLoading}
                            rows={4}
                        />
                    </div>

                    <div className="LifelineEventAddModal_field">
                        <label>{translate('lifeline_event_type')}:</label>
                        <div className="LifelineEventAddModal_radio_group">
                            <label className="LifelineEventAddModal_radio_label">
                                <input
                                    type="radio"
                                    name="eventType"
                                    checked={good}
                                    onChange={() => setGood(true)}
                                    disabled={isLoading}
                                />
                                {translate('lifeline_positive_event')}
                            </label>
                            <label className="LifelineEventAddModal_radio_label">
                                <input
                                    type="radio"
                                    name="eventType"
                                    checked={!good}
                                    onChange={() => setGood(false)}
                                    disabled={isLoading}
                                />
                                {translate('lifeline_negative_event')}
                            </label>
                        </div>
                    </div>

                    <div className="LifelineEventAddModal_field">
                        <label className="LifelineEventAddModal_checkbox_label">
                            <input
                                type="checkbox"
                                checked={level === 2}
                                onChange={(e) => setLevel(e.target.checked ? 2 : 1)}
                                disabled={isLoading}
                            />
                            {translate('lifeline_important_event')}
                        </label>
                    </div>
                </div>

                <div className="LifelineEventAddModal_actions">
                    <button 
                        className="LifelineEventAddModal_cancel"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        {translate('lifeline_cancel')}
                    </button>
                    <button 
                        className="LifelineEventAddModal_save"
                        onClick={handleSave}
                        disabled={isLoading}
                    >
                        {isLoading ? translate('lifeline_loading_save') : translate('lifeline_save_event')}
                    </button>
                </div>
            </div>
        </div>
    );
};